/**
 * Shared Billing Entity Form
 *
 * Presentational form component for creating and editing billing entities.
 * Used by the billing setup wizard, edit modal, and admin billing section.
 */

import { TooltipHelp } from '@features/billing-readiness/components/TooltipHelp';
import { NpiValidationStatus } from '@features/npi-validation';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { looksLikeMailboxAddress } from '../utils/addressHeuristics';

/**
 * Core behavioral health clinician taxonomies
 */
export const TAXONOMY_CODES = [
  { code: '2084P0800X', name: 'Psychiatrist' },
  { code: '2084P0804X', name: 'Child & Adolescent Psychiatrist' },
  { code: '2084P0802X', name: 'Addiction Psychiatrist' },
  { code: '2084A0401X', name: 'Addiction Medicine Psychiatrist' },
  { code: '103T00000X', name: 'Psychologist' },
  { code: '103TC0700X', name: 'Clinical Psychologist' },
  { code: '103TC2200X', name: 'Clinical Child & Adolescent Psychologist' },
  { code: '1041C0700X', name: 'Licensed Clinical Social Worker (LCSW)' },
  { code: '101YP2500X', name: 'Professional Counselor (LPC / LPCC / LCPC)' },
  { code: '101YM0800X', name: 'Mental Health Counselor (LMHC / LCMHC)' },
  { code: '101YA0400X', name: 'Addiction Counselor' },
  { code: '106H00000X', name: 'Marriage & Family Therapist (LMFT)' },
  { code: '103G00000X', name: 'Clinical Neuropsychologist' },
  { code: '363L00000X', name: 'Nurse Practitioner' },
  { code: '363LP0808X', name: 'Psychiatric Nurse Practitioner (PMHNP)' },
  { code: '363A00000X', name: 'Physician Assistant' },
  { code: '364SP0808X', name: 'Psychiatric Clinical Nurse Specialist' },
  { code: '261QM0850X', name: 'Clinic/Center - Adult Mental Health' },
];

/** Flat address shape shared by the billing address and the pay-to address. */
export interface BillingEntityAddressFormData {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

export interface BillingEntityFormData {
  billing_npi: string;
  tax_id: string;
  tax_id_type: string;
  taxonomy_code: string;
  legal_name: string;
  doing_business_as: string;
  /** X12 837P 2010AA — must be a physical street address. */
  billing_address: BillingEntityAddressFormData;
  /** X12 837P 2010AB — where payers mail checks. A PO Box is fine here. */
  pay_to_address: BillingEntityAddressFormData;
  /** Whether the pay-to section is in play at all (drives the payload). */
  has_pay_to_address: boolean;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
}

const emptyAddress = (): BillingEntityAddressFormData => ({
  line1: '',
  line2: '',
  city: '',
  state: '',
  zip: '',
});

export const initialBillingEntityFormData: BillingEntityFormData = {
  billing_npi: '',
  tax_id: '',
  tax_id_type: 'EIN',
  taxonomy_code: '',
  legal_name: '',
  doing_business_as: '',
  billing_address: emptyAddress(),
  pay_to_address: emptyAddress(),
  has_pay_to_address: false,
  contact_name: '',
  contact_email: '',
  contact_phone: '',
};

/** What made the pay-to section appear — the checkbox, or the PO Box nudge. */
export type PayToToggleSource = 'checkbox' | 'po_box_warning';

const PAY_TO_ADDRESS_FIELDS: readonly (keyof BillingEntityAddressFormData)[] = [
  'line1',
  'line2',
  'city',
  'state',
  'zip',
];

interface BillingEntityFormProps {
  data: BillingEntityFormData;
  onChange: (field: keyof BillingEntityFormData, value: string) => void;
  onAddressChange: (field: keyof BillingEntityAddressFormData, value: string) => void;
  /**
   * Pay-to address field edits. Optional so surfaces that predate the pay-to
   * section keep compiling — the section simply stays inert without it.
   */
  onPayToAddressChange?: (field: keyof BillingEntityAddressFormData, value: string) => void;
  /**
   * Pay-to section shown/hidden. `source` tells the parent whether this came
   * from the checkbox or from the PO Box warning's move button, which is the
   * only thing distinguishing the two analytics events.
   */
  onTogglePayTo?: (checked: boolean, source: PayToToggleSource) => void;
  errors?: Partial<Record<string, string>>;
  /** Disable specific fields (e.g., after Stedi verification) */
  disabledFields?: {
    billing_npi?: boolean;
    tax_id?: boolean;
    tax_id_type?: boolean;
  };
  /** Placeholder text for tax ID field (e.g., masked value in edit mode) */
  taxIdPlaceholder?: string;
  /** Helper text override for tax ID field */
  taxIdHelperText?: string;
  /** Whether tax ID is required (true for create, false for edit) */
  taxIdRequired?: boolean;
}

export function BillingEntityForm({
  data,
  onChange,
  onAddressChange,
  onPayToAddressChange,
  onTogglePayTo,
  errors = {},
  disabledFields = {},
  taxIdPlaceholder,
  taxIdHelperText,
  taxIdRequired = true,
}: BillingEntityFormProps) {
  // Advisory only — this never blocks a save. The backend is the authority.
  const billingAddressLooksLikeMailbox = looksLikeMailboxAddress(
    data.billing_address.line1,
    data.billing_address.line2,
  );

  // The component stays pure: "clearing" the pay-to fields on uncheck is
  // emitted through the same onPayToAddressChange the text fields use, so the
  // parent's single reducer keeps owning the state.
  const clearPayToAddress = () => {
    PAY_TO_ADDRESS_FIELDS.forEach(field => onPayToAddressChange?.(field, ''));
  };

  const handleTogglePayTo = (checked: boolean) => {
    if (!checked) {
      clearPayToAddress();
    }
    onTogglePayTo?.(checked, 'checkbox');
  };

  // "Move this to the pay-to address": the mailbox lines belong in 2010AB, and
  // the provider is left with an empty 2010AA street to fill in properly.
  const handleMoveToPayTo = () => {
    onTogglePayTo?.(true, 'po_box_warning');
    onPayToAddressChange?.('line1', data.billing_address.line1);
    onPayToAddressChange?.('line2', data.billing_address.line2);
    onAddressChange('line1', '');
    onAddressChange('line2', '');
  };

  return (
    <Grid container spacing={2}>
      {/* NPI */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="Billing NPI"
          value={data.billing_npi}
          onChange={e => onChange('billing_npi', e.target.value)}
          fullWidth
          required
          disabled={disabledFields.billing_npi}
          error={!!errors.billing_npi}
          helperText={
            errors.billing_npi ||
            (disabledFields.billing_npi
              ? 'Cannot be changed after verification'
              : '10-digit National Provider Identifier')
          }
         slotProps={{ htmlInput: { maxLength: 10 } }}/>
        <NpiValidationStatus npi={data.billing_npi} expectedType="organization" />
      </Grid>

      {/* Tax ID Type */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormControl fullWidth required disabled={disabledFields.tax_id_type}>
          <InputLabel>Tax ID Type</InputLabel>
          <Select
            value={data.tax_id_type}
            label="Tax ID Type"
            onChange={e => onChange('tax_id_type', e.target.value)}
          >
            <MenuItem value="EIN">EIN (Employer Identification Number)</MenuItem>
            <MenuItem value="SSN">SSN (Social Security Number)</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Tax ID */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label={`Tax ID (${data.tax_id_type})`}
          value={data.tax_id}
          onChange={e => onChange('tax_id', e.target.value)}
          fullWidth
          required={taxIdRequired}
          disabled={disabledFields.tax_id}
          error={!!errors.tax_id}
          helperText={
            errors.tax_id ||
            taxIdHelperText ||
            (disabledFields.tax_id
              ? 'Cannot be changed after verification'
              : 'Format: XX-XXXXXXX (EIN) or XXX-XX-XXXX (SSN)')
          }
          placeholder={taxIdPlaceholder}
        />
      </Grid>

      {/* Taxonomy Code */}
      <Grid size={{ xs: 12 }}>
        <Autocomplete
          value={TAXONOMY_CODES.find(t => t.code === data.taxonomy_code) || null}
          onChange={(_event, newValue) => {
            onChange('taxonomy_code', newValue?.code || '');
          }}
          options={TAXONOMY_CODES}
          getOptionLabel={option => `${option.code} - ${option.name}`}
          renderOption={({ key, ...otherProps }, option) => {
            return (
              <li key={key} {...otherProps}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {option.code}
                  </Typography>
                </Box>
              </li>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              label="Taxonomy Code"
              required
              error={!!errors.taxonomy_code}
              helperText={errors.taxonomy_code || 'Provider specialty/credential type'}
            />
          )}
        />
      </Grid>

      {/* Legal Name */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Legal Name"
          value={data.legal_name}
          onChange={e => onChange('legal_name', e.target.value)}
          fullWidth
          required
          error={!!errors.legal_name}
          helperText={errors.legal_name || 'Full legal name of the entity'}
        />
      </Grid>

      {/* Doing Business As (optional) */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Doing Business As (DBA)"
          value={data.doing_business_as}
          onChange={e => onChange('doing_business_as', e.target.value)}
          fullWidth
          helperText="Optional - if different from legal name"
        />
      </Grid>

      {/* Billing Address */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Billing Address
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Your practice&apos;s physical street location. Payers require a street address here — PO
          Boxes are rejected on claims.
        </Typography>
      </Grid>

      {/* Address Line 1 */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Address Line 1"
          value={data.billing_address.line1}
          onChange={e => onAddressChange('line1', e.target.value)}
          fullWidth
          required
          error={!!errors.billing_address}
        />
      </Grid>

      {/* Address Line 2 */}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Address Line 2"
          value={data.billing_address.line2}
          onChange={e => onAddressChange('line2', e.target.value)}
          fullWidth
          helperText="Suite, unit, etc. (optional)"
        />
      </Grid>

      {/* City */}
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="City"
          value={data.billing_address.city}
          onChange={e => onAddressChange('city', e.target.value)}
          fullWidth
          required
          error={!!errors.billing_address}
        />
      </Grid>

      {/* State */}
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="State"
          value={data.billing_address.state}
          onChange={e => onAddressChange('state', e.target.value)}
          fullWidth
          required
          error={!!errors.billing_address}
          helperText="2-letter code (e.g., CA)"
         slotProps={{ htmlInput: { maxLength: 2 } }}/>
      </Grid>

      {/* ZIP */}
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="ZIP Code"
          value={data.billing_address.zip}
          onChange={e => onAddressChange('zip', e.target.value)}
          fullWidth
          required
          error={!!errors.billing_address}
          helperText="XXXXX or XXXXX-XXXX"
        />
      </Grid>

      {/* PO Box advisory — never blocks the save, just gets ahead of the payer
          rejection (Stedi submission error 33) and offers the one-click fix. */}
      {billingAddressLooksLikeMailbox && (
        <Grid size={{ xs: 12 }}>
          <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
            This looks like a PO Box or mailbox. Payers reject claims when the billing address
            isn&apos;t a physical street address — enter your street address here.
            <Box sx={{ mt: 1 }}>
              <Button size="small" onClick={handleMoveToPayTo}>
                Move this to the pay-to address
              </Button>
            </Box>
          </Alert>
        </Grid>
      )}

      {/* Pay-To Address opt-in */}
      <Grid size={{ xs: 12 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={data.has_pay_to_address}
              onChange={e => handleTogglePayTo(e.target.checked)}
            />
          }
          label="Payer checks and mail go to a different address"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Only check this if payers should send payments or letters somewhere other than the address
          above.
        </Typography>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Collapse in={data.has_pay_to_address} unmountOnExit>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }} component="div">
                <TooltipHelp
                  term="Pay-To Address"
                  explanation="The Pay-To Address on insurance claims — where the payer sends payment and correspondence."
                />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Where payers send checks and mail. A PO Box or mailbox service is fine here.
              </Typography>
            </Grid>

            {/* Pay-To Address Line 1 */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Pay-To Address Line 1"
                value={data.pay_to_address.line1}
                onChange={e => onPayToAddressChange?.('line1', e.target.value)}
                fullWidth
                required
                error={!!errors['pay_to_address.line1']}
                helperText={errors['pay_to_address.line1']}
              />
            </Grid>

            {/* Pay-To Address Line 2 */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Pay-To Address Line 2"
                value={data.pay_to_address.line2}
                onChange={e => onPayToAddressChange?.('line2', e.target.value)}
                fullWidth
                helperText="Suite, unit, etc. (optional)"
              />
            </Grid>

            {/* Pay-To City */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Pay-To City"
                value={data.pay_to_address.city}
                onChange={e => onPayToAddressChange?.('city', e.target.value)}
                fullWidth
                required
                error={!!errors['pay_to_address.city']}
                helperText={errors['pay_to_address.city']}
              />
            </Grid>

            {/* Pay-To State */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Pay-To State"
                value={data.pay_to_address.state}
                onChange={e => onPayToAddressChange?.('state', e.target.value)}
                fullWidth
                required
                error={!!errors['pay_to_address.state']}
                helperText={errors['pay_to_address.state'] || '2-letter code (e.g., CA)'}
                slotProps={{ htmlInput: { maxLength: 2 } }}
              />
            </Grid>

            {/* Pay-To ZIP */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Pay-To ZIP Code"
                value={data.pay_to_address.zip}
                onChange={e => onPayToAddressChange?.('zip', e.target.value)}
                fullWidth
                required
                error={!!errors['pay_to_address.zip']}
                helperText={errors['pay_to_address.zip'] || 'XXXXX or XXXXX-XXXX'}
              />
            </Grid>
          </Grid>
        </Collapse>
      </Grid>

      {/* Contact Information */}
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Contact Information
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Required for clearinghouse communication about claims
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="Contact Name"
          value={data.contact_name}
          onChange={e => onChange('contact_name', e.target.value)}
          fullWidth
          helperText="Optional"
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="Contact Email"
          type="email"
          value={data.contact_email}
          onChange={e => onChange('contact_email', e.target.value)}
          fullWidth
          required
          error={!!errors.contact_email}
          helperText={errors.contact_email || 'For claim status notifications'}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          label="Contact Phone"
          value={data.contact_phone}
          onChange={e => onChange('contact_phone', e.target.value)}
          fullWidth
          required
          error={!!errors.contact_phone}
          helperText={errors.contact_phone || 'For urgent claim issues'}
        />
      </Grid>
    </Grid>
  );
}
